import { useEffect } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';

import { getListMyChargesQueryKey, listMyCharges } from '../../api/generated/ledger/ledger';
import type { Charge } from '../../api/generated/model';

/**
 * O teto que o contrato permite pedir de uma vez (`limit`, `maximum: 200`), e
 * que `normalizaLimite` (`api/internal/ledger/service.go`) honra sem reduzir.
 *
 * Pedir o maximo **nao** substitui seguir o cursor: ele so faz o extrato de
 * quatro anos caber em uma ida ao servidor em vez de cinco.
 */
const PAGE_SIZE = 200;

/** O extrato do aluno, e o que a tela pode afirmar a partir dele. */
export interface MyChargesResult {
  /** O que chegou ate agora, na ordem em que o servidor devolveu. */
  readonly charges: readonly Charge[];
  /**
   * Verdadeiro **somente** quando nao ha mais cursor a seguir. Enquanto for
   * falso, `charges` e um PREFIXO do extrato: nem soma de dinheiro nem
   * afirmacao de vazio podem sair dele.
   */
  readonly complete: boolean;
  /** A primeira pagina falhou — ou uma do meio, que e o caso mais silencioso. */
  readonly failed: boolean;
}

/**
 * O extrato INTEIRO do aluno, por seguir o cursor ate `nextCursor` nulo.
 *
 * Mora aqui, e nao em cada tela, porque as duas telas que mostram dinheiro do
 * aluno — a home e o portal financeiro — fazem a MESMA conta, e ela so esta
 * certa sobre a colecao completa. Duas copias da regra seriam duas chances de
 * uma delas voltar a somar sobre uma pagina.
 *
 * **Por que nao usar o `charges` que `getMyBilling` ja devolve.** `MyBilling`
 * monta aquele array com `s.ListMyCharges(ctx, nil, defaultPageSize)`, e
 * `defaultPageSize` e 50 (`api/internal/ledger/service.go`). Somar sobre ele
 * SUBESTIMA a divida de quem tem mais de 50 faturas — e as que ficam atras do
 * cursor sao as mais antigas, isto e, justamente as com maior chance de estar
 * `overdue`. O outro campo do mesmo resumo, `outstanding`, e uma soma que o
 * banco faz sobre a tabela inteira (`SumOutstandingByPerson`): eram dois
 * numeros para a mesma divida, e o que a tela escolhia mostrar era o menor.
 * Sobre a colecao completa os dois voltam a bater, e o total exibido pode ser
 * conferido somando o que esta escrito na tela.
 *
 * `refetchInterval` existe para a volta do checkout hospedado: ate o webhook
 * chegar a fatura ainda esta em cobranca, e e a reconsulta que a faz passar
 * para o historico sozinha, sem a tela prometer nada antes do ledger.
 */
export function useAllMyCharges({
  enabled,
  refetchInterval = false,
}: {
  readonly enabled: boolean;
  readonly refetchInterval?: number | false;
}): MyChargesResult {
  const query = useInfiniteQuery({
    queryKey: getListMyChargesQueryKey({ limit: PAGE_SIZE }),
    queryFn: ({ pageParam, signal }) =>
      listMyCharges(
        pageParam === undefined ? { limit: PAGE_SIZE } : { limit: PAGE_SIZE, cursor: pageParam },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled,
    refetchInterval,
  });

  const { hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage } = query;
  useEffect(() => {
    // `isFetchNextPageError` faz parte da condicao de parada, e nao e detalhe:
    // sem ele, a pagina que falha no meio deixa `hasNextPage` verdadeiro e
    // `isFetchingNextPage` de volta em falso, o efeito dispara de novo e a tela
    // entra em tempestade de requisicoes contra o servidor que acabou de
    // recusar.
    if (!hasNextPage || isFetchingNextPage || isFetchNextPageError) return;
    void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage]);

  return {
    charges: (query.data?.pages ?? []).flatMap((page) => page.items),
    complete: query.data !== undefined && !hasNextPage,
    // `isError` fala so da primeira pagina. A que falha NO MEIO e a pior das
    // duas: sem este ramo ela deixa a lista truncada com a mesma cara de lista
    // completa.
    failed: query.isError || isFetchNextPageError,
  };
}
